import { useEffect, useMemo, useState } from "react";
import { theme, card, emptyFrame } from "../theme";
import type { Release } from "../hooks/useVersionCheck";

interface VersionFile {
  version: string;
  deployedAt: string | null;
  releases: Release[];
}

// ── Parsing helpers ───────────────────────────────────────────────────────

type ChangeType = "feat" | "fix" | "maintenance" | "unknown";

interface ParsedRelease {
  version: string;
  date: string;
  type: ChangeType;
  title: string;
  detail: string;
  minorGroup: string; // e.g. "3.31"
}

function fixEncoding(s: string): string {
  return s
    .replace(/ΓÇö/g, "—")
    .replace(/ΓÇô/g, "–")
    .replace(/ΓÇÿ/g, "‘")
    .replace(/ΓÇÖ/g, "’")
    .replace(/ΓÇ£/g, "“")
    .replace(/ΓÇ¥/g, "”");
}

const CONVENTIONAL_PREFIX = /^(feat|fix|docs|chore|refactor|test|perf|build|ci|style|revert)(\([^)]*\))?!?:\s*/i;
const DEPLOY_PREFIX = /^(deploy:\s*)+/i;
const NOISE_PATTERNS = [
  /\[skip (ci|version)\]/i,
  /^chore: (retrigger|update version history)/i,
  /^chore: merge/i,
  /^build:/i,
  /^ci:/i,
  /^docs:/i,
  /^chore: add.*diagnostic/i,
  // Repository plumbing is not a release note: merge commits name the repository and its branches,
  // and demo data, research and documentation are not changes a customer receives (AUD-21).
  /^Merge (pull request|branch|remote-tracking)/i,
  /^docs(\(|:)/i,
  /^(feat|fix|chore)\((demo|research|hooks?|claude-md|ci|build)\)/i,
];

/** Internal tracking references ("(REP-1, 17)", "(AUD-3, 4)") are not for customers. */
const TRACKING_REFS = /\s*\((?:[A-Z]{2,5}-\d+(?:[,;]?\s*(?:to\s+)?(?:[A-Z]{2,5}-)?\d+)*)\)\s*$/;

function parseRelease(r: Release): ParsedRelease | null {
  // A release is customer-facing only when its message carried a Release note line (AUD-21): the
  // note is the entry. null = history only. Entries from before the field existed (no `note` key)
  // keep the earlier curation below, and of those only the feature releases.
  if (r.note !== undefined) {
    const note = typeof r.note === "string" ? fixEncoding(r.note.trim()) : "";
    if (!note) return null;
    const prefix = CONVENTIONAL_PREFIX.exec(fixEncoding(r.summary.trim()).replace(DEPLOY_PREFIX, "").trim());
    const kind = prefix?.[1].toLowerCase();
    const parts = r.version.split(".");
    return {
      version: r.version,
      date: r.date,
      type: kind === "feat" ? "feat" : kind === "fix" || kind === "revert" ? "fix" : "unknown",
      title: capitalize(note),
      detail: "",
      minorGroup: parts.length >= 2 ? `${parts[0]}.${parts[1]}` : r.version,
    };
  }
  const legacy = parseLegacyRelease(r);
  return legacy && legacy.type === "feat" ? legacy : null;
}

function parseLegacyRelease(r: Release): ParsedRelease | null {
  let raw = fixEncoding(r.summary.trim());

  // Strip leading deploy: prefixes
  raw = raw.replace(DEPLOY_PREFIX, "").trim();

  // Check for noise entries — skip them
  if (NOISE_PATTERNS.some((p) => p.test(raw))) return null;

  // Extract conventional commit type
  let type: ChangeType = "unknown";
  const match = CONVENTIONAL_PREFIX.exec(raw);
  if (match) {
    const t = match[1].toLowerCase();
    if (t === "feat") type = "feat";
    else if (t === "fix" || t === "revert") type = "fix";
    else type = "maintenance";
    raw = raw.slice(match[0].length).trim();
  }

  // Also skip maintenance / noise after stripping prefix
  if (type === "maintenance") return null;
  if (type === "unknown" && /^(chore|docs|build|ci|test|style)\b/i.test(raw)) return null;

  // Split into title + detail (separated by two newlines, or "  " double space in summary)
  // version.json summaries use "  " as newline stand-in when truncated
  const [titleRaw, ...detailParts] = raw.split(/\n\n|\s{2,}/);
  const title = titleRaw.replace(TRACKING_REFS, "").trim();
  if (!title) return null;

  // Strip trailing Co-Authored-By lines from detail
  const detail = detailParts
    .join("\n")
    .replace(/Co-Authored-By:.*$/gim, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  // Minor group e.g. "3.31" from "3.31.4"
  const parts = r.version.split(".");
  const minorGroup = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : r.version;

  return {
    version: r.version,
    date: r.date,
    type: type === "unknown" ? "feat" : type,
    title: capitalize(title),
    detail,
    minorGroup,
  };
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ── Badge ─────────────────────────────────────────────────────────────────

const TYPE_META: Record<ChangeType, { label: string; bg: string; color: string }> = {
  feat:        { label: "Feature",     bg: theme.color.accentBg,    color: theme.color.accentText },
  fix:         { label: "Fix",         bg: "#fef3c7",               color: "#b45309" },
  maintenance: { label: "Maintenance", bg: "var(--c-panelAlt,#f3f4f6)", color: theme.color.subtle },
  unknown:     { label: "Update",      bg: "var(--c-panelAlt,#f3f4f6)", color: theme.color.subtle },
};

function TypeBadge({ type }: { type: ChangeType }) {
  const m = TYPE_META[type];
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 2,
      background: m.bg, color: m.color, textTransform: "uppercase", letterSpacing: "0.06em",
      flexShrink: 0,
    }}>
      {m.label}
    </span>
  );
}

// ── Release card ──────────────────────────────────────────────────────────

function ReleaseCard({ release, isLatest }: { release: ParsedRelease; isLatest: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = !!release.detail;

  return (
    <div style={{
      ...card,
      padding: "14px 18px",
      borderLeft: isLatest ? `3px solid ${theme.color.accent}` : undefined,
      marginBottom: 0,
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 2,
          background: isLatest ? theme.color.accentBg : "var(--c-panelAlt,#f3f4f6)",
          color: isLatest ? theme.color.accentText : theme.color.subtle,
          fontVariantNumeric: "tabular-nums",
        }}>
          v{release.version}
        </span>
        <TypeBadge type={release.type} />
        {isLatest && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 2,
            background: (theme.color.successText ?? "#059669") + "22",
            color: theme.color.successText ?? "#059669",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Latest
          </span>
        )}
        <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>
          {fmtDate(release.date)}
        </span>
      </div>

      {/* Title */}
      <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, lineHeight: 1.45 }}>
        {release.title}
      </div>

      {/* Expandable detail */}
      {hasDetail && (
        <>
          {expanded && (
            <div style={{
              fontSize: 12, color: theme.color.subtle, marginTop: 8,
              lineHeight: 1.65, whiteSpace: "pre-wrap",
            }}>
              {release.detail}
            </div>
          )}
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{
              marginTop: 6, background: "none", border: "none", padding: 0,
              fontSize: 11, color: theme.color.accent, cursor: "pointer", fontWeight: 600,
            }}
          >
            {expanded ? "Show less ▲" : "Show more ▼"}
          </button>
        </>
      )}
    </div>
  );
}

// ── Minor version group ───────────────────────────────────────────────────

function VersionGroup({ group, releases, latestVersion }: {
  group: string;
  releases: ParsedRelease[];
  latestVersion: string;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const isCurrentMinor = releases.some((r) => r.version === latestVersion);

  return (
    <div style={{ marginBottom: 28 }}>
      {/* Group header */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%",
          background: "none", border: "none", cursor: "pointer", padding: "0 0 10px 0",
          borderBottom: `1px solid ${theme.color.border}`, marginBottom: 12,
        }}
      >
        <span style={{
          fontSize: 13, fontWeight: 700, color: theme.color.heading,
          fontVariantNumeric: "tabular-nums",
        }}>
          {group}.x series
        </span>
        {isCurrentMinor && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 2,
            background: theme.color.accentBg, color: theme.color.accentText,
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Current
          </span>
        )}
        <span style={{ fontSize: 11, color: theme.color.faint }}>
          {releases.length} release{releases.length !== 1 ? "s" : ""}
        </span>
        <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>
          {collapsed ? "▼" : "▲"}
        </span>
      </button>

      {!collapsed && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {releases.map((r) => (
            <ReleaseCard key={r.version} release={r} isLatest={r.version === latestVersion} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

type FilterType = "all" | "feat" | "fix";

export function WhatsNewPage() {
  const [data, setData] = useState<VersionFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterType>("all");

  useEffect(() => {
    fetch(`/version.json?t=${Date.now()}`)
      .then((r) => r.json() as Promise<VersionFile>)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Parse and filter releases
  const parsed = useMemo<ParsedRelease[]>(() => {
    if (!data) return [];
    return data.releases
      .map(parseRelease)
      .filter((r): r is ParsedRelease => r !== null);
  }, [data]);

  const filtered = useMemo(() => {
    let list = parsed;
    if (filter !== "all") list = list.filter((r) => r.type === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (r) => r.title.toLowerCase().includes(q) || r.detail.toLowerCase().includes(q)
      );
    }
    return list;
  }, [parsed, filter, query]);

  // Group by minor version
  const groups = useMemo(() => {
    const map = new Map<string, ParsedRelease[]>();
    for (const r of filtered) {
      const g = map.get(r.minorGroup) ?? [];
      g.push(r);
      map.set(r.minorGroup, g);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const latestVersion = data?.version ?? "";
  const featCount = parsed.filter((r) => r.type === "feat").length;
  const fixCount  = parsed.filter((r) => r.type === "fix").length;

  return (
    <div className="page-pad" style={{ flex: 1, overflowY: "auto" }}>
      <div>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading }}>
            What's new
          </h2>
          {data && (
            <div style={{ fontSize: 12, color: theme.color.subtle }}>
              Current version: <strong style={{ fontVariantNumeric: "tabular-nums" }}>v{data.version}</strong>
              {data.deployedAt && (
                <> · deployed {fmtDate(data.deployedAt.split("T")[0])}</>
              )}
            </div>
          )}
        </div>

        {/* Stats strip */}
        {!loading && parsed.length > 0 && (
          <div style={{
            display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap",
          }}>
            {[
              { label: "Total releases", value: parsed.length },
              { label: "Features",       value: featCount },
              { label: "Bug fixes",      value: fixCount },
              { label: "Version series", value: groups.length },
            ].map(({ label, value }) => (
              <div key={label} style={{
                ...card, padding: "10px 16px", flex: "1 1 120px", textAlign: "center",
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: theme.color.heading }}>{value}</div>
                <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Search + filter */}
        {!loading && parsed.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
            <input
              type="search"
              placeholder="Search release notes…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                flex: "1 1 200px", padding: "8px 12px", fontSize: 13,
                border: `1px solid ${theme.color.border}`, borderRadius: 3,
                background: theme.color.panel, color: theme.color.text, outline: "none",
              }}
            />
            {(["all", "feat", "fix"] as FilterType[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: "7px 14px", fontSize: 12, fontWeight: 600, borderRadius: 3,
                  border: `1px solid ${filter === f ? theme.color.accent : theme.color.border}`,
                  background: filter === f ? theme.color.accentBg : "transparent",
                  color: filter === f ? theme.color.accentText : theme.color.subtle,
                  cursor: "pointer",
                }}
              >
                {f === "all" ? "All" : f === "feat" ? "Features" : "Bug fixes"}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {loading && (
          <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading release history…</div>
        )}

        {!loading && groups.length === 0 && (
          <div style={{ ...emptyFrame, color: theme.color.faint, fontSize: 13, padding: 40 }}>
            {query || filter !== "all" ? "No releases match your search." : "Nothing to show yet - releases appear here when they carry a customer-facing note."}
          </div>
        )}

        {groups.map(([group, releases]) => (
          <VersionGroup
            key={group}
            group={group}
            releases={releases}
            latestVersion={latestVersion}
          />
        ))}
      </div>
    </div>
  );
}
